import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Autoplay from 'embla-carousel-autoplay';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';

import { Skeleton } from '@/components/ui/skeleton';
import { IBanner } from '../modules/home/types/Banner';
import { IProductInfo } from '../modules/home/types/Menu';
import {
  findCategoryInMenu,
  findProductInMenu,
} from '../modules/home/libs/menuLookup';
import { followDeeplink, parseDeeplink } from '../utils/navigateToTarget';
import { custonSooner } from './CustopmSooner';
import { cn } from '@/lib/utils';

/**
 * Куда ведёт баннер.
 *
 * Только target: его бэк формирует сам при привязке товара — "product/1073"
 * или "category/85". Список products в ответе тоже есть, но для перехода не
 * используется: у баннера с "category/85" в products всё равно лежит блюдо,
 * и переход по нему увёл бы не туда.
 */
const getBannerDeeplink = (banner: IBanner) => parseDeeplink(banner.target);

export function BannerCarousel({
  banners,
  isLoading,
  menu = [],
}: {
  banners: IBanner[];
  isLoading: boolean;
  // Меню филиала — им проверяем, что привязанное к баннеру блюдо ещё
  // существует. Пустое (например, филиал ещё не определён) — проверку
  // пропускаем: за неё есть второй рубеж на самой карточке блюда.
  menu?: { id: number; menu: IProductInfo[] }[];
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const plugin = React.useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false }),
  );
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [viewportWidth, setViewportWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const visibleCount = Math.floor(viewportWidth / 351);
  const showIndicators = banners.length > visibleCount;

  React.useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  // Товар привязан, но его уже нет в меню — единственный случай, когда
  // пользователю нужно объяснение.
  const showUnavailable = () => {
    custonSooner.error(t('product-unavailable'));
    navigate('/');
  };

  const handleBannerClick = (banner: IBanner) => {
    const link = getBannerDeeplink(banner);

    // Пустой target — баннер чисто рекламный, вести с него некуда. Это не
    // ошибка: молча игнорируем нажатие.
    if (!link) {
      return;
    }

    // Блюдо могли удалить или снять с продажи уже после того, как баннер
    // сверстали. Ведём на пустую карточку не за что.
    const isMissing =
      menu.length > 0 &&
      ((link.kind === 'product' && !findProductInMenu(menu, link.productId)) ||
        (link.kind === 'category' &&
          !findCategoryInMenu(menu, link.categoryId)));

    if (isMissing) {
      showUnavailable();
      return;
    }

    followDeeplink(link, navigate);
  };

  if (isLoading) {
    return (
      <div className="w-full h-[84px]">
        <Skeleton className="w-full h-[84px] rounded-2xl bg-white" />
      </div>
    );
  }

  if (!banners.length) return null;

  const isSingle = banners.length === 1;

  return (
    <div className="w-full">
      <Carousel
        plugins={isSingle ? [] : [plugin.current]}
        className="w-full overflow-visible"
        opts={{ loop: !isSingle, align: 'start' }}
        setApi={setApi}
      >
        <CarouselContent className="ml-0">
          {banners.map((banner, index) => (
            <CarouselItem
              key={index}
              // Одиночный баннер тянется на всю ширину, поэтому слайду нужен
              // basis-full: с basis-auto ширина считается по контенту, а
              // контент внутри задан в 100% — WebKit такую петлю схлопывает
              // в ноль, и на iOS баннер пропадает.
              className={cn(
                'shrink-0 pl-0',
                isSingle ? 'basis-full' : 'basis-auto',
              )}
            >
              <div
                onClick={() => handleBannerClick(banner)}
                className={cn(
                  'overflow-hidden rounded-[16px] transition-all duration-500',
                  // Без таргета баннер никуда не ведёт — не показываем и
                  // курсор-указатель, чтобы он не выглядел кликабельным.
                  getBannerDeeplink(banner) ? 'cursor-pointer' : 'cursor-default',
                  // Отступ нужен только между слайдами; у одиночного он бы
                  // вытолкнул баннер за край вьюпорта.
                  !isSingle && 'mr-2',
                )}
                style={{ width: isSingle ? '100%' : '343px', height: '84px' }}
              >
                <img
                  src={banner.image2}
                  alt={`banner-${index}`}
                  className="h-full w-full object-initial"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {showIndicators && (
        <div className="mt-2 flex items-center justify-center gap-2">
          {banners.map((_, index) => (
            <span
              key={index}
              className={cn(
                'rounded-full transition-all duration-300',
                index === current
                  ? 'h-[6px] w-[24px] bg-[#2BB747]'
                  : 'h-[6px] w-[6px] bg-[#E1E4E8]',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
